import { FC, useEffect, useState } from "react";
import "./ModalChatToolArticles.scss";
import { Button, Card, Col, FormGroup, Row } from "react-bootstrap";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import ModalDefault from "../../../../components/ModalDefault/ModalDefault";
import SkeletonTable from "../../../../components/SkeletonTable/SkeletonTable";
import { customStyles } from "../../../../models/SelectCustomStyles";
import { ChatToolArticlesService } from "../../../../services/Chats/ChatToolArticlesService";
import { ArticlesService } from "../../../../services/Articles/ArticlesService";
import { SectionsService } from "../../../../services/Articles/Sections/SectionsService";
import { SubsectionsService } from "../../../../services/Articles/Subsections/SubsectionsService";
import { FinancialsService } from "../../../../services/Financial/FinancialsService";
import { useAgentsCategories } from "../../../../providers/Agents/AgentsCategories/AgentsCategoriesProvider";
import { ChatToolModel } from "../../../../models/Chat/ChatTool/ChatToolModel";
import { ChatToolArticleModel } from "../../../../models/Chat/ChatTool/ChatToolArticleModel";

interface ModalChatToolArticlesProps {
  show: boolean;
  onClose: () => void;
  tool: ChatToolModel | null;
}

const ROLE_OPTIONS = [
  { label: "Padrão", value: "default" },
  { label: "Roteador (router)", value: "router" },
  { label: "Detalhe (detail)", value: "detail" },
];

const ModalChatToolArticles: FC<ModalChatToolArticlesProps> = ({
  show,
  onClose,
  tool,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoadingArticles, setIsLoadingArticles] = useState<boolean>(false);
  const [links, setLinks] = useState<ChatToolArticleModel[]>([]);

  const [sectionOptions, setSectionOptions] = useState<any[]>([]);
  const [subsectionOptions, setSubsectionOptions] = useState<any[]>([]);
  const [articleOptions, setArticleOptions] = useState<any[]>([]);
  const [financialOptions, setFinancialOptions] = useState<any[]>([]);

  const [selectedCategory, setSelectedCategory] = useState<any | null>(null);
  const [selectedSection, setSelectedSection] = useState<any | null>(null);
  const [selectedSubsection, setSelectedSubsection] = useState<any | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<any | null>(null);
  const [selectedFinancial, setSelectedFinancial] = useState<any | null>(null);
  const [selectedRole, setSelectedRole] = useState<any | null>(ROLE_OPTIONS[0]);

  const { categoriesList, handleListCategories } = useAgentsCategories();

  const linkService = new ChatToolArticlesService();
  const articlesService = new ArticlesService();
  const sectionsService = new SectionsService();
  const subsectionsService = new SubsectionsService();
  const financialsService = new FinancialsService();

  const categoryOptions =
    categoriesList?.map((c: any) => ({ label: c.name, value: c.id })) ?? [];

  const loadLinks = async () => {
    if (!tool?.id) return;
    setIsLoading(true);
    const [response, error] = await linkService.list({
      filter: { chat_tool_id: tool.id, only_active: true },
      withPaginate: false,
    });
    setIsLoading(false);
    if (error) {
      toast.error(response?.message || error);
      return;
    }
    setLinks(response?.items ?? []);
  };

  const loadFinancials = async () => {
    const [response, error] = await financialsService.list({
      filter: { only_active: true },
      withPaginate: false,
    });
    if (error) return;
    setFinancialOptions(
      (response?.items ?? []).map((f: any) => ({ label: f.name, value: f.id }))
    );
  };

  const loadSections = async (categoryId: number) => {
    const [response, error] = await sectionsService.list({
      filter: { only_active: true, category_id: categoryId },
      withPaginate: false,
    });
    if (error) return;
    setSectionOptions(
      (response?.items ?? []).map((s: any) => ({ label: s.name, value: s.id }))
    );
  };

  const loadSubsections = async (sectionId: number) => {
    const [response, error] = await subsectionsService.list({
      filter: { only_active: true, section_id: sectionId },
      withPaginate: false,
    });
    if (error) return;
    setSubsectionOptions(
      (response?.items ?? []).map((s: any) => ({ label: s.name, value: s.id }))
    );
  };

  const loadArticles = async (filter: any) => {
    if (!filter?.category_id) {
      setArticleOptions([]);
      return;
    }
    setIsLoadingArticles(true);
    const [response, error] = await articlesService.list({
      filter: { only_active: true, ...filter },
      withPaginate: false,
    });
    setIsLoadingArticles(false);
    if (error) return;
    setArticleOptions(
      (response?.items ?? []).map((a: any) => ({
        label: `#${a.id} - ${a.title}`,
        value: a.id,
      }))
    );
  };

  const resetForm = () => {
    setSelectedSection(null);
    setSelectedSubsection(null);
    setSelectedArticle(null);
    setSelectedFinancial(null);
    setSelectedRole(ROLE_OPTIONS[0]);
    setSectionOptions([]);
    setSubsectionOptions([]);
    setArticleOptions([]);
  };

  useEffect(() => {
    if (show && tool?.id) {
      resetForm();
      setSelectedCategory(null);
      handleListCategories();
      loadFinancials();
      loadLinks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, tool?.id]);

  // Pré-seleciona a categoria da tool assim que a lista de categorias chega
  // e já carrega as seções/artigos dessa categoria.
  useEffect(() => {
    if (show && tool?.category_id && categoriesList?.length && !selectedCategory) {
      const option = categoryOptions.find((c) => c.value === tool.category_id);
      if (option) {
        setSelectedCategory(option);
        loadSections(option.value);
        loadArticles({ category_id: option.value });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, tool?.category_id, categoriesList]);

  const handleSelectCategory = (option: any) => {
    setSelectedCategory(option);
    setSelectedSection(null);
    setSelectedSubsection(null);
    setSelectedArticle(null);
    setSubsectionOptions([]);
    if (option?.value) {
      loadSections(option.value);
      loadArticles({ category_id: option.value });
    } else {
      setSectionOptions([]);
      setArticleOptions([]);
    }
  };

  const handleSelectSection = (option: any) => {
    setSelectedSection(option);
    setSelectedSubsection(null);
    setSelectedArticle(null);
    setSubsectionOptions([]);
    if (option?.value) loadSubsections(option.value);
    loadArticles({
      category_id: selectedCategory?.value,
      section_id: option?.value,
    });
  };

  const handleSelectSubsection = (option: any) => {
    setSelectedSubsection(option);
    setSelectedArticle(null);
    loadArticles({
      category_id: selectedCategory?.value,
      section_id: selectedSection?.value,
      subsection_id: option?.value,
    });
  };

  const handleAdd = async () => {
    if (!selectedArticle?.value) {
      toast.warning("Selecione um artigo.");
      return;
    }

    setIsSaving(true);
    const [response, error] = await linkService.fetch({
      chat_tool_id: tool?.id,
      article_id: selectedArticle.value,
      financial_id: selectedFinancial?.value ?? null,
      role: selectedRole?.value ?? "default",
    });
    setIsSaving(false);

    if (error) {
      toast.error(response?.message || error);
      return;
    }

    toast.success("Vínculo adicionado com sucesso!");
    setSelectedArticle(null);
    setSelectedFinancial(null);
    setSelectedRole(ROLE_OPTIONS[0]);
    loadLinks();
  };

  const handleDelete = (item: ChatToolArticleModel) => {
    Swal.fire({
      title: "<strong>Atenção?</strong>",
      html: `<span>Deseja remover o vínculo do artigo #${item.article_id}?</span>`,
      icon: "question",
      confirmButtonText: "Sim, remover!",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      confirmButtonColor: "var(--primary)",
    }).then(async (x: any) => {
      if (x.isConfirmed) {
        const [response, error] = await linkService.delete(item.id || 0);
        if (error) {
          toast.error(response?.message || error);
          return;
        }
        toast.success("Vínculo removido com sucesso!");
        loadLinks();
      }
    });
  };

  const renderTable = () => {
    if (isLoading) return <SkeletonTable />;

    return (
      <div className="table-responsive">
        <table className="table table-flush m-0 w-100">
          <thead className="thead-light">
            <tr>
              <th className="text-uppercase opacity-7" scope="col">
                Artigo
              </th>
              <th className="text-uppercase opacity-7" scope="col">
                Financeira
              </th>
              <th className="text-uppercase opacity-7 text-center" scope="col">
                Papel
              </th>
              <th className="text-uppercase opacity-7 text-center" scope="col">
                Ação
              </th>
            </tr>
          </thead>
          <tbody>
            {links?.length > 0 ? (
              links.map((x: ChatToolArticleModel) => (
                <tr key={x.id} className="text-sm">
                  <td className="text-wrap">
                    #{x.article_id} - {x.article_title}
                  </td>
                  <td>{x.financial_name ?? "— (qualquer)"}</td>
                  <td className="text-center">{x.role}</td>
                  <td className="text-center">
                    <FontAwesomeIcon
                      icon={faTrash}
                      className="cursor-pointer"
                      onClick={() => handleDelete(x)}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="text-center text-sm">
                  Nenhum artigo vinculado
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <ModalDefault
      title={`Artigos vinculados${tool?.name ? ` - ${tool.name}` : ""}`}
      icon="article"
      show={show}
      onClose={onClose}
      sizeModal={"xl"}
      showFooter={true}
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div className="ModalChatToolArticles" data-testid="ModalChatToolArticles">
        <Row>
          <Col md={4}>
            <FormGroup className="form-group">
              <label>CATEGORIA</label>
              <ReactSelect
                isSearchable
                isClearable
                options={categoryOptions}
                value={selectedCategory}
                placeholder="Selecione"
                className="form-control p-0"
                noOptionsMessage={() => "Não há registros"}
                styles={customStyles}
                onChange={(e) => handleSelectCategory(e)}
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup className="form-group">
              <label>SEÇÃO</label>
              <ReactSelect
                isSearchable
                isClearable
                isDisabled={!selectedCategory}
                options={sectionOptions}
                value={selectedSection}
                placeholder="Selecione"
                className="form-control p-0"
                noOptionsMessage={() => "Não há registros"}
                styles={customStyles}
                onChange={(e) => handleSelectSection(e)}
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup className="form-group">
              <label>SUBSEÇÃO</label>
              <ReactSelect
                isSearchable
                isClearable
                isDisabled={!selectedSection}
                options={subsectionOptions}
                value={selectedSubsection}
                placeholder="Selecione"
                className="form-control p-0"
                noOptionsMessage={() => "Não há registros"}
                styles={customStyles}
                onChange={(e) => handleSelectSubsection(e)}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row className="align-items-end">
          <Col md={5}>
            <FormGroup className="form-group">
              <label>ARTIGO *</label>
              <ReactSelect
                isSearchable
                isClearable
                isDisabled={!selectedCategory || isLoadingArticles}
                options={articleOptions}
                value={selectedArticle}
                placeholder={
                  !selectedCategory
                    ? "Selecione uma categoria"
                    : isLoadingArticles
                    ? "Carregando..."
                    : "Selecione o artigo"
                }
                className="form-control p-0"
                noOptionsMessage={() => "Não há registros"}
                styles={customStyles}
                onChange={(e) => setSelectedArticle(e)}
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup className="form-group">
              <label>FINANCEIRA</label>
              <ReactSelect
                isSearchable
                isClearable
                options={financialOptions}
                value={selectedFinancial}
                placeholder="Qualquer"
                className="form-control p-0"
                noOptionsMessage={() => "Não há registros"}
                styles={customStyles}
                onChange={(e) => setSelectedFinancial(e)}
              />
            </FormGroup>
          </Col>
          <Col md={2}>
            <FormGroup className="form-group">
              <label>PAPEL</label>
              <ReactSelect
                options={ROLE_OPTIONS}
                value={selectedRole}
                className="form-control p-0"
                styles={customStyles}
                onChange={(e) => setSelectedRole(e)}
              />
            </FormGroup>
          </Col>
          <Col md={1}>
            <FormGroup className="form-group">
              <Button
                variant="primary mb-0 w-100"
                disabled={isSaving}
                onClick={handleAdd}
                title="Adicionar"
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </FormGroup>
          </Col>
        </Row>

        <Card className="mt-2">
          <Card.Header>
            <h5 className="mb-0">Vínculos</h5>
          </Card.Header>
          <Card.Body>{renderTable()}</Card.Body>
        </Card>
      </div>
    </ModalDefault>
  );
};

export default ModalChatToolArticles;
